import { useState, useCallback, useEffect } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { Alert } from '@material-ui/lab';
import {
    Box, Button, Card, CardContent, CardHeader, CircularProgress, IconButton, Paper,
    Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography, makeStyles
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import StarIcon from '@material-ui/icons/Star';
import dateToString from '../utils/dateToString';

// What a contestant can say about a slot, in the order clicking cycles through
const NOT_AVAILABLE = 0, AVAILABLE = 1, PREFERRED = 2;
const SLOT_STATES = [
    { state: NOT_AVAILABLE, label: 'Not available', Icon: CloseIcon, color: 'textSecondary' },
    { state: AVAILABLE, label: 'Available', Icon: CheckIcon, color: 'primary' },
    { state: PREFERRED, label: 'Preferred', Icon: StarIcon, color: 'secondary' }
];

const useStyles = makeStyles(theme => ({
    slotCell: {
        textAlign: 'center',
        padding: theme.spacing(0.5),
    },
    notAvailable: {
        color: theme.palette.error.main,
    },
    available: {
        color: theme.palette.success.main,
    },
    preferred: {
        // amber, kept apart from the orange the warnings below the table use
        color: '#ffc107',
    },
    dayCell: {
        whiteSpace: 'nowrap',
    }
}));

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// The days come as YYYY-MM-DD, which is read as UTC, so the weekday is taken in UTC too
function dayToString(day) {
    const date = new Date(day);
    return `${day} (${WEEKDAYS[date.getUTCDay()]})`;
}

function countSlots(slots, state) {
    return slots.reduce((count, day) => count + day.filter(slot => slot === state).length, 0);
}

const AvailabilityFormPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const route = useRouteMatch();
    const formId = route.params.formId;

    const [form, setForm] = useState(null);
    const [slots, setSlots] = useState([]);
    const [remark, setRemark] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitTime, setSubmitTime] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMessages, setErrorMessages] = useState(null);
    const [message, setMessage] = useState(null);

    const getForm = useCallback(async () => {
        const response = await fetch(`/api/availability-form/${formId}`, { credentials: 'include' });
        if (response.status !== 200) return setErrorMessages(['Unexpected error']);
        const result = await response.json();
        if (result.status === 'FAILED') return setErrorMessages(result.body.errorMessages);
        setForm(result.body.form);
        setSlots(result.body.response.slots);
        setRemark(result.body.response.remark);
        setSubmitted(result.body.response.submitted);
        setSubmitTime(result.body.response.submitTime);
    }, [formId]);

    useEffect(() => {
        getForm().then(() => setLoading(false));
    }, [getForm]);

    const toggleSlot = (dayIndex, slotIndex) => {
        setSlots(slots => slots.map((day, currentDay) => day.map((slot, currentSlot) =>
            currentDay === dayIndex && currentSlot === slotIndex ? (slot + 1) % SLOT_STATES.length : slot
        )));
    };

    const onSubmit = async () => {
        const response = await fetch(`/api/availability-form/${formId}/response`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ slots, remark })
        });
        if (response.status !== 200) return setMessage({ severity: 'error', text: 'Unexpected error' });
        const result = await response.json();
        if (result.status === 'FAILED') return setMessage({ severity: 'error', text: result.body.errorMessages.join(' ') });
        setSubmitted(true);
        setSubmitTime(Date.now());
        setMessage({ severity: 'success', text: 'Availability submitted' });
    };

    if (loading) return <Box align='center' p={3}><CircularProgress /></Box>;

    if (errorMessages) return (
        <Card>
            <CardHeader title='Error' />
            <CardContent>
                {errorMessages.map((errorMessage, index) => <Typography key={index}>{errorMessage}</Typography>)}
                <Box mt={2}>
                    <Button color='primary' onClick={() => history.push('/')}> Back to home </Button>
                </Box>
            </CardContent>
        </Card>
    );

    const availableCount = countSlots(slots, AVAILABLE) + countSlots(slots, PREFERRED);
    const preferredCount = countSlots(slots, PREFERRED);
    const tooFewAvailable = availableCount < form.minAvailableSlots;
    const tooFewPreferred = preferredCount < form.minPreferredSlots;

    return (
        <>
            <Box mb={3}>
                <Typography variant='h4'>{form.name}</Typography>
                <Typography color='textSecondary'>
                    {form.deadline ? `Please fill in by ${dateToString(form.deadline)}` : 'No deadline'}
                    {submitted && submitTime ? ` · submitted ${dateToString(submitTime)}` : ''}
                </Typography>
            </Box>

            <Box mb={3}>
                <Typography paragraph>
                    The availability form will be for the upcoming week of the tournament only, you need to fill in the dates/times that you are available and preferred. Please note that your match will only take place in at most one time slot per day (and usually one time slot per week), but you can still be a spectator for other sessions.
                </Typography>
                <Typography paragraph>
                    {`Also, note that it is recommended to pick at least ${form.minAvailableSlots} available time slots and ${form.minPreferredSlots} preferred time slots to facilitate the scheduling. On top of that, you are advised to indicate as many time slots as possible.`}
                </Typography>
                <Typography paragraph>
                    All participants need to fill out the form. Otherwise, you will be assumed available in all time slots.
                </Typography>
                <Typography paragraph>
                    Important: If the time slot of your match is marked as available by you but you are not present at the competition, you may be disqualified from the tournament.
                </Typography>
            </Box>

            <Box mb={3}>
                <Typography variant='body2' color='textSecondary'>
                    Click a slot to go from not available to available to preferred.
                </Typography>
                <Box display='flex' flexWrap='wrap' mt={1}>
                    {SLOT_STATES.map(({ state, label, Icon }) => (
                        <Box key={state} display='flex' alignItems='center' mr={3}>
                            <Icon className={[classes.notAvailable, classes.available, classes.preferred][state]} />
                            <Typography variant='body2'>{label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Day</TableCell>
                            {form.timeSlots.map(timeSlot => (
                                <TableCell key={timeSlot} className={classes.slotCell}>{timeSlot}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {form.days.map((day, dayIndex) => (
                            <TableRow key={day}>
                                <TableCell className={classes.dayCell}>{dayToString(day)}</TableCell>
                                {form.timeSlots.map((timeSlot, slotIndex) => {
                                    const state = slots[dayIndex]?.[slotIndex] ?? NOT_AVAILABLE;
                                    const { label, Icon } = SLOT_STATES[state];
                                    return (
                                        <TableCell key={timeSlot} className={classes.slotCell}>
                                            <IconButton
                                                size='small'
                                                title={`${dayToString(day)} ${timeSlot}: ${label}`}
                                                onClick={() => toggleSlot(dayIndex, slotIndex)}
                                            >
                                                <Icon className={[classes.notAvailable, classes.available, classes.preferred][state]} />
                                            </IconButton>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box mt={2}>
                <Typography variant='body2'>
                    {`${availableCount} available (including preferred), ${preferredCount} preferred`}
                </Typography>
            </Box>
            {tooFewAvailable &&
                <Box mt={2}>
                    <Alert severity='warning'>
                        {`Please mark at least ${form.minAvailableSlots} slots as available or preferred, you have ${availableCount}.`}
                    </Alert>
                </Box>
            }
            {tooFewPreferred &&
                <Box mt={2}>
                    <Alert severity='warning'>
                        {`Please mark at least ${form.minPreferredSlots} slots as preferred, you have ${preferredCount}.`}
                    </Alert>
                </Box>
            }

            <Box mt={3}>
                <Typography variant='h6'>Remarks</Typography>
                <Typography>
                    Please include information if you are encountering special circumstances (e.g. if you are traveling and are unable to participate in the contest in the week). Do not include other words (e.g. I prefer Wednesdays / daytime) as they will not be considered.
                </Typography>
            </Box>

            <Box mt={2}>
                <TextField
                    label='Other notes'
                    placeholder='Anything the hosts should know'
                    multiline
                    minRows={3}
                    fullWidth
                    variant='outlined'
                    value={remark}
                    onChange={event => setRemark(event.target.value)}
                />
            </Box>

            <Box mt={3} display='flex' alignItems='center'>
                <Button variant='contained' color='primary' onClick={onSubmit}>
                    {submitted ? 'Update availability' : 'Submit availability'}
                </Button>
                <Box ml={2}>
                    <Button onClick={() => history.push('/')}> Back to home </Button>
                </Box>
            </Box>

            <Snackbar open={message != null} autoHideDuration={3000} onClose={() => setMessage(null)}>
                <Alert onClose={() => setMessage(null)} severity={message?.severity}>{message?.text}</Alert>
            </Snackbar>
        </>
    );
};

export default AvailabilityFormPage;
